import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import {
  ChangePackageSettlementVm,
  ChangePackageSummaryVm,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { ChangePackageStep } from '@/features/dashboard/bookingChanges/routes/changePackageSearch.ts';
import { isZeroMoneyList } from '@/features/dashboard/bookingChanges/utils/money.ts';
import { BookingChangeDetailDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeDetailDto.ts';

/**
 * Kroki przepływu wprowadzania paczki (W1 → W2 → W3): ich kolejność i warunki
 * przejścia dalej.
 *
 * Warunek jest wartością, nie wyjątkiem — ekran dostaje `canProceed` i powód,
 * więc ta sama odpowiedź wyłącza przycisk i tłumaczy w dymku, czego brakuje.
 */

/** Kolejność kroków — jedyne miejsce, w którym „krok 2 z 3” jest zdefiniowane. */
export const CHANGE_PACKAGE_STEPS: ChangePackageStep[] = [
  ChangePackageStep.SUMMARY,
  ChangePackageStep.SETTLEMENT,
  ChangePackageStep.ACCEPTANCE,
];

export const CHANGE_PACKAGE_STEP_COUNT = CHANGE_PACKAGE_STEPS.length;

/** Pozycja kroku licząc od jedynki — „Krok 2 z 3”. */
export const changePackageStepNumber = (step: ChangePackageStep): number =>
  CHANGE_PACKAGE_STEPS.indexOf(step) + 1;

/**
 * Warunek przejścia dalej. `reason` trafia do `title` wygaszonego przycisku,
 * żeby operator wiedział, czego brakuje, a nie tylko że nie da się kliknąć.
 */
export interface StepGuard {
  canProceed: boolean;
  reason?: string;
}

const PROCEED: StepGuard = { canProceed: true };

/** Krok W1 → W2: pusta paczka nie ma czego rozliczać. */
export const summaryStepGuard = (
  summary: ChangePackageSummaryVm,
): StepGuard => {
  if (summary.itemCount === 0) {
    return {
      canProceed: false,
      reason: 'Szkic nie ma żadnej pozycji — nie ma czego wprowadzać.',
    };
  }
  return PROCEED;
};

export interface SettlementStepGuardInput {
  settlement: ChangePackageSettlementVm;
  correctionDueDate: string | null;
  correctionPaymentMethods: PaymentMethod[];
  isSubmitting: boolean;
}

/**
 * Krok W2 → wprowadzenie paczki.
 *
 * Paczka o niezerowej kwocie wymaga terminu i metod płatności korekty — bez
 * nich `resolve` kończy się odmową backendu, więc lepiej powiedzieć to przed
 * kliknięciem niż po. Puste zaznaczenie dokumentów przejściu nie przeszkadza:
 * klient dostaje wtedy link, na którym akceptuje samą zmianę.
 */
export const settlementStepGuard = ({
  settlement,
  correctionDueDate,
  correctionPaymentMethods,
  isSubmitting,
}: SettlementStepGuardInput): StepGuard => {
  if (isSubmitting) {
    return { canProceed: false, reason: 'Trwa wprowadzanie paczki…' };
  }
  if (isZeroMoneyList(settlement.balance)) {
    return PROCEED;
  }
  if (!correctionDueDate) {
    return {
      canProceed: false,
      reason: 'Podaj termin płatności korekty.',
    };
  }
  if (correctionPaymentMethods.length === 0) {
    return {
      canProceed: false,
      reason: 'Wskaż co najmniej jedną metodę płatności korekty.',
    };
  }
  return PROCEED;
};

/**
 * Paczka, której link można wysłać ponownie z kroku W3, albo `null`.
 *
 * Krok W3 pokazuje otwartą paczkę rezerwacji, nie tę z adresu — stary link do
 * zamkniętej paczki, gdy na klienta czeka już nowsza, skończyłby się 409.
 */
export const resendableChangeId = (
  routeChangeId: string,
  openChange: Pick<BookingChangeDetailDto, 'id' | 'resolvedAt'> | null,
): string | null =>
  openChange?.resolvedAt && openChange.id === routeChangeId
    ? openChange.id
    : null;
